import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { SERVICES, COMING_SOON_SLUGS, servicePagePath } from "@/lib/content";
import { priceInfoFor } from "@/lib/servicesContent";
import { fmtMoney } from "@/lib/promo";
import { api } from "@/lib/api";
import Calculator from "@/components/Calculator";

/**
 * /services — central service directory.
 * Quick comparison of every service with links to the dedicated pages.
 * Prices come from the live pricing engine (single source of truth).
 */
export default function ServicesDirectory() {
  const [catalog, setCatalog] = useState([]);
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    api.get("/pricing/config")
      .then(({ data }) => {
        setCatalog(data.services || []);
        setPromo(data.promo || null);
      })
      .catch(() => {});
  }, []);

  const priceInfo = (slug) => priceInfoFor(slug, catalog, promo);

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: SERVICES.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: s.title,
      url: `https://wtddetailing.ca${servicePagePath(s.slug)}`,
    })),
  };

  return (
    <div data-testid="services-directory" className="min-h-screen bg-[#0c0c0f] text-white">
      <Seo
        title="Auto Detailing & Protection Services in Edmonton"
        description="Every WTD Detailing & Tint service in one place: ceramic coating, paint correction, interior detailing, exterior detailing with paint decontamination add-on, spray-on bed liner, headlight restoration and odor removal in Edmonton, Alberta — plus window tint and PPF launching soon. Compare and get an exact price for your vehicle."
        keywords="auto detailing services Edmonton, car detailing Edmonton, ceramic coating Edmonton, paint correction Edmonton, PPF Edmonton"
        path="/services"
        jsonLd={[
          itemList,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
          ]),
        ]}
      />
      <Navbar />

      <main>
        <section className="relative pt-40 pb-16 overflow-hidden isolate">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 grid-bg opacity-40" />
            <div className="absolute left-1/4 top-0 w-[500px] h-[400px] rounded-full bg-[#1F5EFF]/10 blur-[130px]" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#1F5EFF]" />
              <span className="font-mono-alt text-[12px] text-[#1F5EFF]">Services</span>
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-display text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] max-w-3xl"
            >
              Detailing & protection services in Edmonton<span className="text-[#1F5EFF] blue-text-glow">.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-6 text-white/60 text-base md:text-lg max-w-2xl leading-relaxed"
            >
              Every service we run at the studio, in one place. Compare what's
              included, then get an exact price for your specific vehicle in the
              calculator — no generic estimates.
            </motion.p>
          </div>
        </section>

        <section className="relative pb-20">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {SERVICES.map((s, i) => {
                const Icon = s.icon;
                const soon = COMING_SOON_SLUGS.includes(s.slug);
                const pi = priceInfo(s.slug);
                return (
                  <motion.div
                    key={s.slug}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
                  >
                    <Link
                      to={servicePagePath(s.slug)}
                      data-testid={`dir-card-${s.slug}`}
                      className="group flex flex-col h-full bg-[#121216] border border-white/10 rounded-2xl p-7 hover:border-[#1F5EFF]/40 hover:bg-[#141419] transition-colors"
                    >
                      <div className="flex items-start justify-between mb-6">
                        <div className="w-11 h-11 rounded-xl bg-[#1F5EFF]/12 border border-[#1F5EFF]/30 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[#1F5EFF]" strokeWidth={1.8} />
                        </div>
                        {soon ? (
                          <span className="px-3 py-1 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 font-mono-alt text-[10px] uppercase tracking-widest">
                            Coming Soon
                          </span>
                        ) : pi.from != null ? (
                          pi.promoFrom != null ? (
                            <span className="text-right">
                              <span className="font-mono-alt text-[10px] text-white/35 line-through mr-1.5">From {fmtMoney(pi.from)}</span>
                              <span className="font-mono-alt text-[11px] text-emerald-300" data-testid={`dir-promo-price-${s.slug}`}>From {fmtMoney(pi.promoFrom)}</span>
                            </span>
                          ) : (
                            <span className="font-mono-alt text-[11px] text-white/60">
                              From {fmtMoney(pi.from)}
                            </span>
                          )
                        ) : null}
                      </div>
                      <div className="font-mono-alt text-[10px] text-white/40 uppercase tracking-widest">
                        {s.tag}
                      </div>
                      <h2 className="mt-1.5 font-display text-2xl text-white group-hover:text-[#8db2ff] transition-colors">
                        {s.title}
                      </h2>
                      <p className="mt-3 text-sm text-white/55 leading-relaxed flex-1">
                        {s.short}
                      </p>
                      <div className="mt-6 inline-flex items-center gap-2 text-sm text-white/60 group-hover:text-white transition-colors">
                        {soon ? "See launch details" : "View service details"}
                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>

            <div className="mt-16 rounded-2xl border border-white/10 bg-[#121216] p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
              <div>
                <h2 className="font-display text-2xl md:text-3xl font-light text-white">
                  Priced for your exact vehicle.
                </h2>
                <p className="mt-2 text-white/55 text-sm md:text-base max-w-xl">
                  Pick your year, make and model in the calculator — the price you
                  see is the price for your car, with real add-ons and condition
                  options. Large or commercial vehicles get a custom quote.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  data-testid="dir-cta-calculator"
                  className="inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-[#1F5EFF] text-white font-semibold hover:bg-white hover:text-black transition-colors"
                >
                  Get Exact Price <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Full booking flow — the same Calculator 2.0 component, single
            source of truth for pricing/packages/upsells/Grand Opening. */}
        <Calculator />
      </main>

      <Footer />
    </div>
  );
}
